import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listPosts, deletePost, updatePost } from '../services/api';
import { getCurrentUser } from '../services/auth';

function MyListings() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const user = getCurrentUser();

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listPosts({ user_id: user.sub });
      setPosts(data.posts || []);
    } catch (err) {
      console.error('Failed to load your listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (post) => {
    if (
      !window.confirm(
        `Permanently delete "${post.title}"?\n\nThis removes the listing and its photos for good — it cannot be undone.`
      )
    ) {
      return;
    }
    setBusyId(post.post_id);
    try {
      await deletePost(post.post_id);
      setPosts((prev) => prev.filter((p) => p.post_id !== post.post_id));
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete listing.');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleStatus = async (post) => {
    const next = post.status === 'sold' ? 'active' : 'sold';
    setBusyId(post.post_id);
    try {
      await updatePost(post.post_id, { status: next });
      setPosts((prev) =>
        prev.map((p) => (p.post_id === post.post_id ? { ...p, status: next } : p))
      );
    } catch (err) {
      console.error('Status change failed:', err);
      alert('Failed to update status.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>My Listings</h1>
        <Link className="btn btn-primary" to="/dashboard">
          + New Listing
        </Link>
      </div>

      {posts.length === 0 ? (
        <div className="alert alert-info">
          You have no active listings.{' '}
          <Link to="/dashboard">Create one</Link>.
        </div>
      ) : (
        <div className="list-group">
          {posts.map((post) => (
            <div
              key={post.post_id}
              className="list-group-item d-flex justify-content-between align-items-center"
            >
              <div className="d-flex align-items-center">
                {post.images && post.images.length > 0 && (
                  <img
                    src={post.images[0]}
                    alt={post.title}
                    className="hl-thumb me-3"
                    style={{ width: 72, height: 54 }}
                  />
                )}
                <div>
                  <div className="fw-semibold">
                    {post.title}{' '}
                    <span
                      className={`badge ms-1 ${
                        post.status === 'sold' ? 'bg-secondary' : 'bg-success'
                      }`}
                    >
                      {post.status === 'sold' ? 'Sold' : 'Active'}
                    </span>
                  </div>
                  <div className="text-muted small">
                    {post.animal_type.toUpperCase()} • ${post.price || '0'}
                  </div>
                </div>
              </div>
              <div className="btn-group">
                <Link
                  to={`/posts/${post.post_id}`}
                  className="btn btn-sm btn-outline-secondary"
                >
                  View
                </Link>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate(`/posts/${post.post_id}/edit`)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={busyId === post.post_id}
                  onClick={() => handleToggleStatus(post)}
                >
                  {post.status === 'sold' ? 'Mark Active' : 'Mark Sold'}
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  disabled={busyId === post.post_id}
                  onClick={() => handleDelete(post)}
                >
                  {busyId === post.post_id ? '…' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyListings;
